import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import {
    Select,
    Info,
    Tooltip,
    TooltipHeader,
    openExternalBrowser,
} from 'react-wot-bootstrap'

import { ONE_SECOND, MQ_BREAKPOINTS } from 'constants'
import { mq } from 'helpers'

import { createAnnouncement, updateAnnouncement } from 'state/actions/announcements'

import { TextArea } from 'containers/ClanLanding/components/TextArea/TextArea'

import {
    ANNOUNCEMENT_EDIT_TEXTAREA_HINT_CHARS,
    ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD,
    ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD_EXAMPLE,
    ANNOUNCEMENT_EDIT_GROUP_TYPE_CAPTION,
    ANNOUNCEMENT_EDIT_GROUP_TYPE_ITEMS,
    ANNOUNCEMENT_EDIT_INFO,
    ANNOUNCEMENT_EDIT_PRIVACY_CAPTION,
    ANNOUNCEMENT_EDIT_PRIVACY_LINK,
    ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD_TITLE_TOOLTIP,
    ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD_BODY_TOOLTIP,
    ANNOUNCEMENT_EDIT_GROUP_TYPE_TOOLTIP,
    ANNOUNCEMENT_EDIT_GROUP_TYPE_ITEMS_TOOLTIP,
    ANNOUNCEMENT_EDIT_INFO_TOOLTIP,
} from '../../translations'

import { FooterEditControls } from '../Footer/FooterEditControls'

import {
    ANNOUNCEMENT_MODE,
    ANNOUNCEMENT_ERROR,
    DISABLING_DURATION,
    FOOTER_EDIT_CONTROLS_STATE,
    TOOLTIP_IDS,
    GROUP_LIST,
} from '../../constants'

import styles from './AnnouncementEditor.scss'

const TEXT_AREA_HEIGHT_SMALL = 220
const TEXT_AREA_HEIGHT_BIG = 270

const getItemsForSelect = (groupList) => (
    groupList.map((type) => ({
        caption: ANNOUNCEMENT_EDIT_GROUP_TYPE_ITEMS[type],
        value: type,
        tooltipId: TOOLTIP_IDS.EDITOR_GROUP_TYPE_ITEMS[type],
    }))
)

class AnnouncementEditorComponent extends React.Component {
    static propTypes = {
        openedAnnouncement: PropTypes.number,
        announcementError: PropTypes.oneOf(Object.values(ANNOUNCEMENT_ERROR)),
        privacyPolicyLink: PropTypes.string.isRequired,
        maxMessageLength: PropTypes.number.isRequired,
        defaultMessageLifetime: PropTypes.number.isRequired,
        groupList: PropTypes.array.isRequired,
        createAnnouncement: PropTypes.func.isRequired,
        updateAnnouncement: PropTypes.func.isRequired,

        item: PropTypes.object,
        announcementMode: PropTypes.oneOf(Object.values(ANNOUNCEMENT_MODE)).isRequired,
        onChangeMode: PropTypes.func.isRequired,
    }

    state = {
        editedText: this.props.item ? this.props.item.body : '',
        groupType: this.props.item ? this.props.item.group_type : this.props.groupList[0],
        textAreaHeight: TEXT_AREA_HEIGHT_SMALL,
        savingButtonState: FOOTER_EDIT_CONTROLS_STATE.WITHOUT_RESTRICTION,
        expiredAt: (Date.now() + this.props.defaultMessageLifetime * ONE_SECOND) / ONE_SECOND,
    }

    componentDidMount() {
        this.updateTextAreaHeight()
        window.addEventListener('resize', this.updateTextAreaHeight)
        this.handleError()

        this.textareaRef.focus()
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.editedText !== this.state.editedText) {
            this.checkSavingButtonState()
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateTextAreaHeight)
        clearTimeout(this.timerId)
    }

    setTextAreaRef = (c) => {
        this.textareaRef = c
    }

    handleError() {
        const { announcementError } = this.props

        clearTimeout(this.timerId)

        const isForbiddenError = announcementError === ANNOUNCEMENT_ERROR.FORBIDDEN_WORDS
        const isCreateError = announcementError === ANNOUNCEMENT_ERROR.EDIT
        const isEditError = announcementError === ANNOUNCEMENT_ERROR.CREATE

        if (isForbiddenError) {
            this.setState({
                savingButtonState: FOOTER_EDIT_CONTROLS_STATE.FORBIDDEN_WORDS,
            })
        } else if (isCreateError || isEditError) {
            this.setState({
                savingButtonState: FOOTER_EDIT_CONTROLS_STATE.DISABLED,
            })

            this.timerId = setTimeout(this.checkSavingButtonState, DISABLING_DURATION)
        }
    }

    checkSavingButtonState = () => {
        const { maxMessageLength } = this.props
        const { editedText } = this.state

        const savingButtonState = editedText.length > maxMessageLength ?
            FOOTER_EDIT_CONTROLS_STATE.MAX_CHARS :
            FOOTER_EDIT_CONTROLS_STATE.WITHOUT_RESTRICTION

        this.setState({
            savingButtonState,
        })
    }

    updateTextAreaHeight = () => {
        const textAreaHeight = mq(MQ_BREAKPOINTS.M) ?
            TEXT_AREA_HEIGHT_BIG :
            TEXT_AREA_HEIGHT_SMALL

        this.setState({ textAreaHeight })
    }

    handleSaving = () => {
        const {
            item,
            announcementMode,
            createAnnouncement,
            updateAnnouncement,
        } = this.props

        const {
            editedText,
            groupType,
            expiredAt,
        } = this.state

        const data = {
            message: editedText,
            groupType,
            expiredAt,
        }

        if (announcementMode === ANNOUNCEMENT_MODE.CREATE) {
            createAnnouncement(data)
        } else if (announcementMode === ANNOUNCEMENT_MODE.EDIT) {
            updateAnnouncement({
                ...data,
                messageId: item.message_id,
            })
        }
    }

    handleCancel = () => {
        const { openedAnnouncement, onChangeMode } = this.props
        onChangeMode(openedAnnouncement ? ANNOUNCEMENT_MODE.VIEW : ANNOUNCEMENT_MODE.CARD)
    }

    updateEditedText = (e) => {
        const editedText = e.target.value
        this.setState({ editedText })
    }

    changeAccessLevel = (value) => {
        this.setState({ groupType: value })
    }

    handlePrivacyLinkOpening = (e) => {
        const { privacyPolicyLink } = this.props

        e.preventDefault()
        openExternalBrowser(privacyPolicyLink)
    }

    renderTooltips = () => {
        const groupTypeItems = Object.keys(GROUP_LIST).map((key) => (
            <Tooltip
                key={TOOLTIP_IDS.EDITOR_GROUP_TYPE_ITEMS[key]}
                id={TOOLTIP_IDS.EDITOR_GROUP_TYPE_ITEMS[key]}
            >
                {ANNOUNCEMENT_EDIT_GROUP_TYPE_ITEMS_TOOLTIP[key]}
            </Tooltip>
        ))

        return (
            <div>
                <Tooltip id={TOOLTIP_IDS.EDITOR_HINT_BOLD}>
                    <TooltipHeader>
                        {ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD_TITLE_TOOLTIP}
                    </TooltipHeader>
                    {ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD_BODY_TOOLTIP}
                </Tooltip>
                <Tooltip id={TOOLTIP_IDS.EDITOR_GROUP_TYPE}>
                    {ANNOUNCEMENT_EDIT_GROUP_TYPE_TOOLTIP}
                </Tooltip>
                <Tooltip id={TOOLTIP_IDS.EDITOR_INFO}>
                    {ANNOUNCEMENT_EDIT_INFO_TOOLTIP}
                </Tooltip>
                {groupTypeItems}
            </div>
        )
    }

    render() {
        const { announcementMode, maxMessageLength, groupList } = this.props

        const {
            editedText,
            groupType,
            expiredAt,
            textAreaHeight,
            savingButtonState,
        } = this.state

        const charLength = editedText.length
        const time = moment(expiredAt * ONE_SECOND).format('DD.MM.YYYY')

        const highlightedTextClassNames = classNames(
            styles.highlightedText,
            (charLength > maxMessageLength) && styles.highlightedText__exceeded,
        )

        return (
            <div className={styles.base}>
                <div className={styles.content}>
                    <TextArea
                        ref={this.setTextAreaRef}
                        height={textAreaHeight}
                        value={editedText}
                        className={styles.textarea}
                        onChange={this.updateEditedText}
                    />
                    <div className={styles.textareaHint}>
                        <span>
                            {ANNOUNCEMENT_EDIT_TEXTAREA_HINT_CHARS(
                                <span className={highlightedTextClassNames}>
                                    {charLength}
                                </span>,
                                maxMessageLength,
                            )}
                        </span>
                        <span
                            data-tip data-for={TOOLTIP_IDS.EDITOR_HINT_BOLD}
                            className={styles.boldHint}
                        >
                            {ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD(
                                <span className={styles.highlightedText}>
                                    {ANNOUNCEMENT_EDIT_TEXTAREA_HINT_BOLD_EXAMPLE}
                                </span>
                            )}
                        </span>
                    </div>
                    <div className={styles.groupType}>
                        <Select
                            items={getItemsForSelect(groupList)}
                            selectedItem={groupType}
                            onItemChange={this.changeAccessLevel}
                            caption={ANNOUNCEMENT_EDIT_GROUP_TYPE_CAPTION}
                            captionPosition="top"
                            tooltipId={TOOLTIP_IDS.EDITOR_GROUP_TYPE}
                        />
                    </div>
                    <div
                        data-tip data-for={TOOLTIP_IDS.EDITOR_INFO}
                        className={styles.announcementInfo}
                    >
                        {ANNOUNCEMENT_EDIT_INFO(time)}
                        <div className={styles.infoIcon}>
                            <Info />
                        </div>
                    </div>
                    <div className={styles.privacyCaption}>
                        {ANNOUNCEMENT_EDIT_PRIVACY_CAPTION(
                            <a
                                className={styles.externalLink}
                                onClick={this.handlePrivacyLinkOpening}
                            >
                                {ANNOUNCEMENT_EDIT_PRIVACY_LINK}
                            </a>
                        )}
                    </div>
                </div>
                <div className={styles.footer}>
                    <FooterEditControls
                        announcementMode={announcementMode}
                        state={savingButtonState}
                        onSave={this.handleSaving}
                        onCancel={this.handleCancel}
                    />
                </div>
                {this.renderTooltips()}
            </div>
        )
    }
}

const stateToProps = (state) => {
    return {
        openedAnnouncement: state.announcements.openedAnnouncement,
        announcementError: state.announcements.error,
        privacyPolicyLink: state.announcements.settings.privacy_policy_link,
        maxMessageLength: state.announcements.settings.max_message_length,
        defaultMessageLifetime: state.announcements.settings.default_message_lifetime,
        groupList: state.announcements.settings.group_list,
    }
}

export const dispatchToProps = (dispatch) => ({
    createAnnouncement: (data) => dispatch(createAnnouncement(data)),
    updateAnnouncement: (data) => dispatch(updateAnnouncement(data)),
})

export const AnnouncementEditor = connect(stateToProps, dispatchToProps)(AnnouncementEditorComponent)



// WEBPACK FOOTER //
// ./containers/ClanLanding/components/AnnouncementsCard/components/AnnouncementEditor/AnnouncementEditor.jsx